import { useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { toast } from "react-toastify";

import logoHeader from "../../assets/fluxmed-logo-horizontal.png";
import {
  CircularLoading,
  ContentBoxTitle,
  LargeContentBox,
} from "../../components/common";
import {
  FormAutoComplete,
  FormButtonGroup,
  FormMultipleAutoComplete,
  FormSelectInput,
  FormStack,
  FormTextInput,
} from "../../components/form";
import { useSessionContext } from "../../contexts/SessionContext";
import { handleKeyPress } from "../../utils/useKeyPress";
import { RegistrationSuccess } from "./components/RegistrationSuccess";
import {
  GetPractitioner,
  GetPractitionerRole,
  PutOrganization,
  PutPractitioner,
  PutPractitionerRole,
  UpdatePractitionerRole,
} from "@/services/fhir";
import { currentFeatures, endpoints, oids } from "@/configs/Settings";
import TermText from "../TermsOfUse/components/TermText";
import { Info, Person, Badge, VerifiedUser } from "@mui/icons-material";
import { GridCheckCircleIcon } from "@mui/x-data-grid";
import { UFValueSet } from "../professionalregister/components/form/ValueSet";
import { useValueSet } from "@/hooks/useValueSet";
import dayjs from "dayjs";
import { PostContract } from "@/services/fhir/contract/PostContract";

const conselhosOptions = [
  {
    display: "CRM - Conselho Regional de Medicina",
    value: "CRM",
    descricao: "Medicina",
    code: 1,
  },
  {
    display: "COREN - Conselho Regional de Enfermagem",
    value: "COREN",
    descricao: "Enfermagem",
    code: 2,
  },
  {
    display: "CRO - Conselho Regional de Odontologia",
    value: "CRO",
    descricao: "Odontologia",
    code: 3,
  },
  {
    display:
      "CREFITO - Conselho Regional de Fisioterapia e Terapia Ocupacional",
    value: "CREFITO",
    descricao: "Fisioterapia e Terapia Ocupacional",
    code: 4,
  },
  {
    display: "CRP - Conselho Regional de Psicologia",
    value: "CRP",
    descricao: "Psicologia",
    code: 5,
  },
  {
    display: "CRF - Conselho Regional de Farmácia",
    value: "CRF",
    descricao: "Farmácia",
    code: 6,
  },
  {
    display: "CRN - Conselho Regional de Nutricionistas",
    value: "CRN",
    descricao: "Nutrição",
    code: 7,
  },
  {
    display: "CRBM - Conselho Regional de Biomedicina",
    value: "CRBM",
    descricao: "Biomedicina",
    code: 8,
  },
  {
    display: "CREFONO - Conselho Regional de Fonoaudiologia",
    value: "CREFONO",
    descricao: "Fonoaudiologia",
    code: 9,
  },
  {
    display: "CREF - Conselho Regional de Educação Física",
    value: "CREF",
    descricao: "Educação Física",
    code: 10,
  },
];

const crmv = [
  {
    display: "CRMV - Conselho Regional de Medicina Veterinária",
    value: "CRMV",
    descricao: "Medicina Veterinária",
    code: 1,
  },
];

const steps = ["Dados Pessoais", "Termos de Uso", "Conclusão"];

interface ICPFFormInput {
  displayName: string;
  classe: string | undefined;
  specialty: string;
  number: string;
  uf: string;
  userData: {
    name: string;
    cpf: string;
    email: string;
    phoneNumber: string;
  };
}

// Componente de seção com título e ícone
function SectionHeader({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" width="100%">
      <Box
        sx={{
          p: 1,
          borderRadius: 2,
          bgcolor: "primary100.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Stack spacing={0.5}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          color="neutral800.main"
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="neutral500.main">
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

// Componente de campo de exibição de dados
function DataDisplayField({ label, value }: { label: string; value: string }) {
  return (
    <Stack
      sx={{
        p: 1.5,
        bgcolor: "neutral100.main",
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "neutral200.main",
      }}
    >
      <Typography variant="caption" color="neutral500.main" fontWeight={500}>
        {label}
      </Typography>
      <Typography variant="body2" color="neutral800.main" fontWeight={500}>
        {value || "—"}
      </Typography>
    </Stack>
  );
}

export function RegisterCPF({
  onBackToSelection,
}: {
  onBackToSelection: () => void;
}) {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [checkbox, setCheckbox] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [cpfAlreadyRegistered, setCpfAlreadyRegistered] = useState(false);
  const { user } = useSessionContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchTerm, setSearchTerm] = useState("");

  const options = currentFeatures.defaultApp ? conselhosOptions : crmv;

  const { options: specialtiesOptions } = useValueSet({
    valueSet: "CBO",
    searchTerm: searchTerm,
    customUrl: "https://fluxmed.cloud/fhir/r4/ValueSet/CBO-S",
  });

  const specialties = specialtiesOptions?.map((item) => ({
    value: item.code,
    label: `${item.display} - ${item.code}`,
    display: item.display,
  }));

  const handleOpenPortal = () => {
    window.open(endpoints.XDSB_domain, "_blank");
  };

  const defaultValues: ICPFFormInput = {
    displayName: "",
    specialty: "",
    number: "",
    userData: {
      name: user?.name || "",
      cpf: user?.username || "",
      email: user?.email || "",
      phoneNumber: user?.phone || "",
    },
    classe: currentFeatures.defaultApp ? undefined : crmv[0]?.value,
    uf: "",
  };

  const methods = useForm<ICPFFormInput>({ defaultValues });
  const { handleSubmit, control, getValues } = methods;

  useEffect(() => {
    const verifyCPF = async () => {
      setVerifying(true);
      try {
        await GetPractitionerRole({
          id: `${user.username}-${oids.cpf}-${user?.username}`,
        });
        setCpfAlreadyRegistered(true);
      } catch {
        setCpfAlreadyRegistered(false);
      }
      setVerifying(false);
    };
    verifyCPF();
  }, [user?.username]);

  const onSubmit = async (data: ICPFFormInput) => {
    if (!checkbox) {
      toast.error(
        "A aceitação dos Termos de Uso e Política de Privacidade é obrigatória para continuar."
      );
      return;
    }

    setLoading(true);

    const auxOrganization = {
      resourceType: "Organization",
      id: `${oids.cnpj}-${user?.username}`,
      extension: [
        {
          url: "https://fluxmed.cloud/StructureDefinition/OrganizationManager",
          extension: [
            {
              url: "id",
              valueIdentifier: {
                system: "https://fluxmed.cloud/creator-id-system",
                value: user?.username,
              },
            },
            {
              url: "name",
              valueString: user.name,
            },
            {
              url: "purpose",
              valueString: "socio",
            },
          ],
        },
      ],
      identifier: [
        {
          system: "urn:oid:2.16.840.1.113883.13.237",
          value: user?.username,
        },
      ],
      active: true,
      type: [
        {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/organization-type",
              code: "bus",
              display: "Non-Healthcare Business or Corporation",
            },
          ],
        },
      ],
      contact: [
        {
          address: {
            line: [],
            country: "BRASIL",
          },
        },
        {
          telecom: [
            {
              system: "phone",
              value: user.phone,
            },
            {
              system: "email",
              value: user.email,
            },
          ],
        },
      ],
      name: data.displayName || user.name,
      alias: [data.displayName || user.name],
    };

    const auxContract = {
      resourceType: "Contract",
      extension: [
        {
          url: "https://fluxmed.com.br/hapi/fhir/R5/extensions/subscriptionType",
          valueString: "Profissional autônomo - Acesso gratuito",
        },
        {
          url: "https://fluxmed.com.br/hapi/fhir/R5/extensions/externalReference",
          valueString: "1",
        },
      ],
      identifier: [
        {
          system: "https://fluxmed.com.br/hapi/fhir/R5/contract-ids",
          value: "1620149873",
        },
        {
          system: "https://fluxmed.com.br/hapi/fhir/R5/contract-type",
          value: "subscription",
        },
      ],
      status: "executed",
      contentDerivative: {
        coding: [
          {
            system: "https://fluxmed.com.br/hapi/fhir/R5/contract-origins",
            code: "fluxmed",
          },
        ],
      },
      authority: [
        {
          reference: `Organization/${auxOrganization.id}`,
        },
      ],
      type: {
        coding: [
          {
            system: "https://fluxmed.com.br/hapi/fhir/R5/contract-types",
            code: "subscription",
          },
        ],
      },
      term: [
        {
          identifier: {
            system: "https://fluxmed.com.br/hapi/fhir/R5/maxPraticioners",
            value: "1",
          },
          issued: dayjs().toISOString(),
          applies: {
            start: dayjs().toISOString(),
          },
          text: "Profissional autônomo - Período grátis",
          offer: {
            type: {
              coding: [
                {
                  system:
                    "https://fluxmed.com.br/hapi/fhir/R5/contract-offer-types",
                  code: "subscription",
                },
              ],
            },
          },
          asset: [
            {
              valuedItem: [
                {
                  entityCodeableConcept: {
                    coding: [
                      {
                        system:
                          "https://fluxmed.com.br/hapi/fhir/R5/subscription-types",
                        code: "subscription-fee",
                      },
                    ],
                  },
                  quantity: {
                    value: 1,
                    unit: "subscription",
                  },
                  unitPrice: {
                    value: 0,
                    currency: "BRL",
                  },
                },
              ],
            },
          ],
        },
      ],
    };

    try {
      const practitioner =
        (await GetPractitioner(user.username, true)) ||
        (await PutPractitioner({
          name: user.name,
          identifier: user.username,
          birthDate: user.birthDate,
        }));

      await PutOrganization(auxOrganization);

      const practitionerRole = await PutPractitionerRole({
        organizationId: auxOrganization.id,
        organizationDisplay: auxOrganization.name,
        practitionerId: practitioner.id,
        practitionerDisplay: user.name,
      });

      await UpdatePractitionerRole(practitionerRole, {
        conselho: data?.classe,
        numero: data?.number,
        uf: data?.uf,
        speciality: currentFeatures.defaultApp
          ? data?.specialty
          : [
              {
                value: "1",
                label: data?.specialty || "Veterinária",
                display: data?.specialty || "Veterinária",
              },
            ],
      });
      await PostContract({ data: auxContract });

      setCurrentStep(2);
    } catch (err) {
      console.error("Erro:", err);
      toast.error("Não foi possível finalizar o cadastro");
    } finally {
      setLoading(false);
    }
  };

  // Estado de verificação
  if (verifying) {
    return (
      <LargeContentBox>
        <Stack
          width="100%"
          spacing={3}
          alignItems="center"
          justifyContent="center"
          sx={{ minHeight: "300px" }}
        >
          <CircularLoading />
          <Typography variant="body2" color="neutral500.main">
            Verificando seus dados...
          </Typography>
        </Stack>
      </LargeContentBox>
    );
  }

  // CPF já cadastrado
  if (cpfAlreadyRegistered) {
    return (
      <LargeContentBox>
        <Stack alignItems="center" spacing={4} sx={{ py: 4 }}>
          <img src={logoHeader} alt="Logo fluxmed" width={185} height="auto" />

          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: "success100.main",
              borderRadius: 3,
              textAlign: "center",
              maxWidth: 480,
            }}
          >
            <GridCheckCircleIcon
              sx={{ color: "success.main", fontSize: 64, mb: 2 }}
            />
            <Typography
              variant="h6"
              fontWeight={600}
              color="success800.main"
              gutterBottom
            >
              Cadastro já existente
            </Typography>
            <Typography variant="body2" color="success700.main">
              Você já possui uma clínica cadastrada com este CPF. Acesse a
              plataforma para gerenciar seus dados.
            </Typography>
          </Paper>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={onBackToSelection}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenPortal}
            >
              Acessar plataforma
            </Button>
          </Stack>
        </Stack>
      </LargeContentBox>
    );
  }

  return (
    <FormProvider {...methods}>
      <LargeContentBox variant="content-fit">
        <Stack maxWidth={600} spacing={3} sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
          {/* Logo */}
          <Stack alignItems="center">
            <img
              src={logoHeader}
              alt="Logo fluxmed"
              width={160}
              height="auto"
            />
          </Stack>

          {/* Stepper de progresso */}
          <Stepper
            activeStep={currentStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{
              "& .MuiStepLabel-label": {
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Dados Pessoais */}
          {currentStep === 0 && (
            <Stack spacing={3}>
              {/* Seção: Dados do Usuário */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  bgcolor: "neutral0.main",
                  border: "1px solid",
                  borderColor: "neutral200.main",
                  borderRadius: 2,
                }}
              >
                <Stack spacing={2.5}>
                  <SectionHeader
                    icon={<Person sx={{ color: "primary.main" }} />}
                    title="Dados Pessoais"
                    subtitle="Confirme suas informações cadastradas"
                  />

                  <Divider />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <DataDisplayField
                      label="Nome"
                      value={defaultValues.userData.name}
                    />
                    <DataDisplayField
                      label="CPF"
                      value={defaultValues.userData.cpf}
                    />
                    <DataDisplayField
                      label="E-mail"
                      value={defaultValues.userData.email}
                    />
                    <DataDisplayField
                      label="Telefone"
                      value={defaultValues.userData.phoneNumber}
                    />
                  </Box>
                </Stack>
              </Paper>

              {/* Seção: Identificação da Clínica */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  bgcolor: "neutral0.main",
                  border: "1px solid",
                  borderColor: "neutral200.main",
                  borderRadius: 2,
                }}
              >
                <Stack spacing={2.5}>
                  <SectionHeader
                    icon={<Badge sx={{ color: "primary.main" }} />}
                    title="Identificação da Clínica"
                    subtitle="Defina como os pacientes verão você"
                  />

                  <Divider />

                  <FormTextInput
                    name="displayName"
                    control={control}
                    label="Nome de exibição"
                    required
                  />
                </Stack>
              </Paper>

              {/* Seção: Registro Profissional */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  bgcolor: "neutral0.main",
                  border: "1px solid",
                  borderColor: "neutral200.main",
                  borderRadius: 2,
                }}
              >
                <Stack spacing={2.5}>
                  <SectionHeader
                    icon={<VerifiedUser sx={{ color: "primary.main" }} />}
                    title="Registro Profissional"
                    subtitle="Informe seu conselho de classe"
                  />

                  <Divider />

                  <FormSelectInput
                    control={control}
                    name="classe"
                    defaultValue={
                      currentFeatures.defaultApp ? "CRM" : crmv[0]?.value
                    }
                    label="Conselho de classe"
                    selectOptions={options}
                    required
                  />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 120px" },
                      gap: 2,
                    }}
                  >
                    <FormTextInput
                      name="number*"
                      control={control}
                      label="Número no conselho"
                      required
                    />
                    <FormAutoComplete
                      name="uf"
                      control={control}
                      label="UF"
                      selectOptions={UFValueSet}
                      required
                    />
                  </Box>

                  {currentFeatures.defaultApp ? (
                    <FormMultipleAutoComplete
                      variant="filled"
                      name="specialty"
                      control={control}
                      onInputChange={(e: any) => {
                        const isNumeric = /^\d*$/.test(e?.target?.value);
                        if (!isNumeric) {
                          setSearchTerm?.(e?.target?.value);
                        }
                      }}
                      label="Especialidade"
                      selectOptions={specialties}
                      required
                    />
                  ) : (
                    <FormTextInput
                      name="specialty"
                      control={control}
                      label="Especialidade (Opcional)"
                      required
                    />
                  )}
                </Stack>
              </Paper>

              {/* Botões de ação */}
              <FormButtonGroup
                onGoBackButtonClick={onBackToSelection}
                onNextButtonClick={() => setCurrentStep(1)}
                goBackButtonText="Voltar"
                nextButtonText="Continuar"
                loading={loading}
              />
            </Stack>
          )}

          {/* Step 1: Termos de Uso */}
          {currentStep === 1 && (
            <form
              onKeyDown={(event) =>
                handleKeyPress(event, loading, handleSubmit(onSubmit))
              }
            >
              <Stack spacing={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    bgcolor: "neutral0.main",
                    border: "1px solid",
                    borderColor: "neutral200.main",
                    borderRadius: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <ContentBoxTitle>
                      Termos de Uso e Política de Privacidade
                    </ContentBoxTitle>

                    <Box
                      sx={{
                        maxHeight: { xs: "300px", sm: "400px" },
                        overflowY: "auto",
                        p: 2,
                        bgcolor: "neutral100.main",
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "neutral200.main",
                      }}
                    >
                      <TermText termOf="owner" />
                    </Box>

                    <Divider />

                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={checkbox}
                            onChange={() => setCheckbox(!checkbox)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Li e concordo com os Termos de Uso e Política de
                            Privacidade
                          </Typography>
                        }
                      />
                    </FormGroup>

                    {!checkbox && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: "warning100.main",
                          borderRadius: 1.5,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <Info
                          sx={{
                            color: "warning600.main",
                            fontSize: 20,
                            mt: 0.25,
                          }}
                        />
                        <Typography variant="body2" color="warning800.main">
                          Para continuar, você precisa aceitar os Termos de Uso
                          e Política de Privacidade. Ao recusar, você não poderá
                          acessar a plataforma.
                        </Typography>
                      </Paper>
                    )}
                  </Stack>
                </Paper>

                <FormButtonGroup
                  maxWidth
                  onGoBackButtonClick={() => setCurrentStep(0)}
                  onNextButtonClick={handleSubmit(onSubmit)}
                  goBackButtonText="Voltar"
                  nextButtonText="Aceitar e Finalizar"
                  loading={loading}
                  disabled={!checkbox}
                />
              </Stack>
            </form>
          )}

          {/* Step 2: Sucesso */}
          {currentStep === 2 && (
            <RegistrationSuccess displayName={getValues("displayName")} />
          )}
        </Stack>
      </LargeContentBox>
    </FormProvider>
  );
}
